/* eslint-disable jsx-a11y/no-autofocus */
import type { DropdownLayoutValueOption, PopoverProps, TooltipProps } from '@wix/design-system';
import {
  AutoComplete,
  Dropdown,
  FormField,
  Input,
  listItemSelectBuilder,
  MultiSelect,
  Text,
} from '@wix/design-system';
import type { SyntheticEvent } from 'react';
import React, { useContext, useMemo, useState } from 'react';

import { EditorContext } from './../../../../ricos-context';
import { Link_Target, type Node } from './../../../../ricos-schema/legacy/wix/rich_content/v1';
import type { TFunction } from './../../../../ricos-types/commonTypes';
import { useBiNext } from './../../../bi-context-provider/bi-context-provider';
import type { NamedLink } from './../../../link/types';
import { LINK_MODAL_ID } from './../../../link-modal/constants';

import {
  SECTION_MULTISELECT,
  SECTION_SELECT,
  SECTION_TYPE_SELECT,
} from '../../data-hooks/data-hooks';
import type { DropdownOption } from '../types';
import { SectionEmptyState } from './empty-state';
import { useSectionOptions } from './use-options';

type Option = DropdownOption & { node: Node; prefix?: any };

type Props = {
  autoCompletePopoverProps: PopoverProps;
  autoFocus: boolean;
  dropDownPopoverProps: PopoverProps;
  link: NamedLink;
  onSelect: (anchor: string, defaultName: string | undefined) => void;
  onSubmit: React.EventHandler<SyntheticEvent>;
  t: TFunction;
  tooltipProps: Partial<TooltipProps>;
  setMultipleSections: React.Dispatch<React.SetStateAction<NamedLink[]>>;
  multipleSections: NamedLink[];
};

export const SectionDesktop: React.FC<Props> = ({
  autoCompletePopoverProps,
  autoFocus,
  dropDownPopoverProps,
  link,
  tooltipProps,
  multipleSections,
  onSelect,
  onSubmit,
  t,
  setMultipleSections,
}) => {
  const logBi = useBiNext({ pluginId: 'link', modalId: LINK_MODAL_ID });
  const { sections, sectionTypeOptions } = useSectionOptions();
  const { adapter } = useContext(EditorContext);
  const enableMultipleSelection =
    adapter.tiptapEditor?.state?.selection?.empty &&
    !adapter.getEditorCommands().hasLinkInSelection();

  const sectionOptions = useMemo(
    () =>
      sections.map(section =>
        Object.assign(
          listItemSelectBuilder({
            ellipsis: true,
            titleMaxLines: 2,
            id: section.id,
            label: section.title,
            prefix: section.prefixAvatar,
            size: 'small',
            subtitle: section.subtitle,
            title: section.title,
            tooltipProps: {
              ...tooltipProps,
              children: section.title,
              enterDelay: 1_000,
              placement: 'top',
            },
          }),
          {
            node: section.node,
            prefix: section.prefix,
          }
        )
      ),
    [sections, tooltipProps]
  );

  const [selectedTypeOption, setSelectedTypeOption] = useState<DropdownOption>(
    sectionTypeOptions[0]
  );
  const [selectedOption, setSelectedOption] = useState<Option | null>(
    // Initially "all" option types are selected so any found option should be present in `filteredOptions` below:
    () => (link.anchor && sectionOptions.find(option => option.node.id === link.anchor)) || null
  );
  const [searchPrefix, setSearchPrefix] = useState<any>(selectedOption?.prefix || null);
  const [searchText, setSearchText] = useState<string>(selectedOption?.label || '');
  const filteredOptions = useMemo(() => {
    const lowerSearchText = searchText.trim().toLowerCase();
    return sectionOptions.filter(
      option =>
        (selectedTypeOption.id === 'all' || option.node.type === selectedTypeOption.id) &&
        (selectedOption ||
          !lowerSearchText ||
          option.label?.toLowerCase().includes(lowerSearchText))
    );
  }, [sectionOptions, searchText, selectedTypeOption]);

  const onChangeInput = (value: string) => {
    setSearchPrefix(null);
    setSearchText(value);
    setSelectedOption(null);
  };

  const onSelectTypeOption = (option: DropdownOption) => {
    setSelectedTypeOption(option as Option);
    logBi({
      dataHook: SECTION_TYPE_SELECT,
      value: `${option.id}`,
    });
  };

  const onSelectValueOption = (option: DropdownLayoutValueOption) => {
    setSearchPrefix((option as Option).prefix);
    setSearchText(option.label || searchText);
    setSelectedOption(option as Option);
    onSelect((option as Option).node.id, option.label);
    logBi({ dataHook: SECTION_SELECT, value: option.label });
  };

  const onEnterPressed: React.EventHandler<SyntheticEvent> = evt => {
    if (selectedOption) {
      onSelect(selectedOption.node.id, selectedOption.label);
      onSubmit(evt);
    }
    if (multipleSections.length > 0 && !isOptionMarked) {
      onSubmit(evt);
      setIsOptionMarked(false);
    }
  };

  if (sectionOptions.length === 0) {
    return <SectionEmptyState t={t} />;
  }

  const [isOptionMarked, setIsOptionMarked] = useState(false);

  const onMultiSelectOption = (option: DropdownLayoutValueOption) => {
    setIsOptionMarked(false);
    setMultipleSections(prev => [
      ...prev,
      {
        anchor: (option as Option).node.id,
        defaultName: option.label,
        target: Link_Target.BLANK,
        url: undefined,
      },
    ]);
    logBi({ dataHook: SECTION_MULTISELECT, value: option.label });
  };

  const onRemoveTag = (tagId: string) =>
    setMultipleSections(prevTags => prevTags.filter(currTag => currTag.anchor !== tagId));

  return (
    <>
      <FormField label={t('LinkSettings_Sections_TypeDropdown_Label')} labelPlacement="top">
        <Dropdown
          dataHook={SECTION_TYPE_SELECT}
          onSelect={onSelectTypeOption}
          options={sectionTypeOptions}
          popoverProps={dropDownPopoverProps}
          selectedId={selectedTypeOption.id}
          size="small"
          valueParser={option => option.label}
        />
      </FormField>
      <FormField
        label={t(
          enableMultipleSelection
            ? 'LinkSettings_Sections_MultiSelect_Label'
            : 'LinkSettings_Sections_AutoComplete_Label'
        )}
        labelPlacement="top"
      >
        {enableMultipleSelection ? (
          <MultiSelect
            autoFocus={autoFocus}
            dataHook={SECTION_MULTISELECT}
            dropdownWidth="auto"
            emptyStateMessage={
              <Text skin="disabled" size="small">
                {searchText
                  ? t('LinkSettings_Sections_AutoComplete_NoResults_Search', { searchText })
                  : t('LinkSettings_Sections_AutoComplete_NoResults_All')}
              </Text>
            }
            minWidthPixels="252px"
            onChange={evt => onChangeInput(evt.target.value)}
            onEnterPressed={onEnterPressed}
            options={filteredOptions}
            placeholder={t('LinkSettings_Sections_MultiSelect_Placeholder')}
            popoverProps={autoCompletePopoverProps}
            prefix={searchPrefix && <Input.IconAffix>{searchPrefix}</Input.IconAffix>}
            size="small"
            textOverflow="ellipsis"
            value={searchText}
            onSelect={onMultiSelectOption}
            onRemoveTag={onRemoveTag}
            tags={multipleSections.map(section => ({
              id: section.anchor || '',
              label: section?.defaultName || '',
            }))}
            onOptionMarked={option => {
              if (option) {
                setIsOptionMarked(true);
              }
            }}
          />
        ) : (
          <AutoComplete
            autoFocus={autoFocus}
            dataHook={SECTION_SELECT}
            dropdownWidth="auto"
            emptyStateMessage={
              <Text skin="disabled" size="small">
                {searchText
                  ? t('LinkSettings_Sections_AutoComplete_NoResults_Search', { searchText })
                  : t('LinkSettings_Sections_AutoComplete_NoResults_All')}
              </Text>
            }
            focusOnSelectedOption
            markedOption={selectedOption?.id}
            minWidthPixels="252px"
            onChange={evt => onChangeInput(evt.target.value)}
            onEnterPressed={onEnterPressed}
            onSelect={onSelectValueOption}
            options={filteredOptions}
            placeholder={t('LinkSettings_Sections_AutoComplete_Placeholder')}
            popoverProps={autoCompletePopoverProps}
            prefix={searchPrefix && <Input.IconAffix>{searchPrefix}</Input.IconAffix>}
            selectedId={selectedOption?.id}
            size="small"
            textOverflow="ellipsis"
            value={searchText}
          />
        )}
      </FormField>
    </>
  );
};
